import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Apple, Play, ArrowDown } from "lucide-react";
import { MaskLines, Reveal } from "./Reveal";
import { PhoneMock } from "./PhoneMock";

const StoreBadge = ({ testid, icon: Icon, small, big }) => (
  <div
    data-testid={testid}
    aria-disabled="true"
    title="Coming soon"
    className="group relative flex cursor-not-allowed items-center gap-3 border-2 border-paper/50 bg-ink px-5 py-3"
  >
    <span className="absolute -right-2.5 -top-2.5 bg-orange px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-paper">
      Soon
    </span>
    <Icon size={26} className="text-paper/60" />
    <span className="text-left leading-none">
      <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-paper/40">{small}</span>
      <span className="block font-bebas text-xl tracking-wide text-paper/80">{big}</span>
    </span>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-ink pt-[70px]"
    >
      {/* halftone parallax backdrop */}
      <motion.div style={{ y: bgY }} className="pointer-events-none absolute inset-0 halftone-paper opacity-40" />
      <div className="pointer-events-none absolute -left-20 top-40 h-72 w-72 bg-orange/20 blur-[120px]" />

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:py-24">
        {/* left copy */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="mb-6 inline-flex items-center gap-2 border-2 border-paper/30 px-3 py-1.5">
              <span className="h-2 w-2 bg-orange" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-paper/70">
                Field ops for street teams
              </span>
            </div>
          </Reveal>

          <MaskLines
            className="font-bebas uppercase text-paper"
            lineClass="text-6xl sm:text-7xl lg:text-[7.5vw] leading-[0.82] tracking-tight"
            lines={["Run the", <span key="o" className="text-orange">street.</span>, "Own the block."]}
          />

          <Reveal delay={0.5}>
            <p className="mt-8 max-w-xl font-mono text-sm leading-relaxed text-paper/60 sm:text-base">
              GROUNDWORK is the mobile command center for brand ambassadors and field crews. Geo-verified
              check-ins, live shift tracking, photo proof, and instant reporting — built for the pavement, not
              the office.
            </p>
          </Reveal>

          <Reveal delay={0.65}>
            <div id="download" className="mt-10 flex flex-wrap gap-4">
              <StoreBadge testid="appstore-badge" icon={Apple} small="Download on the" big="APP STORE" />
              <StoreBadge testid="playstore-badge" icon={Play} small="Get it on" big="GOOGLE PLAY" />
            </div>
          </Reveal>

          <Reveal delay={0.8}>
            <div className="mt-10 flex flex-wrap items-center gap-x-10 gap-y-4">
              {[
                ["12K+", "Shifts logged"],
                ["98%", "Check-in accuracy"],
                ["LA · NYC", "+ Remote"],
              ].map(([n, l]) => (
                <div key={l}>
                  <p className="font-bebas text-4xl leading-none text-orange">{n}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-paper/50">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* right phone */}
        <div className="lg:col-span-5">
          <motion.div style={{ y: phoneY }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.4 }}
            >
              <PhoneMock />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <a
        href="#features"
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-paper/50 md:flex"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.span>
        Scroll
      </a>
    </section>
  );
};
